import React from 'react';
import { AppBar, Toolbar, IconButton, Box, Typography, Container, Tooltip } from '@mui/material';
import ReceiptIcon from '@mui/icons-material/Receipt';
import TaskIcon from '@mui/icons-material/Task';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import PieChartIcon from '@mui/icons-material/PieChart';
import FinancialSummary from './FinancialSummary/FinancialSummary';
import PreAuth from './PreAuth/PreAuth';
import ChangeCapture from './ChangeCapture/ChangeCapture';
import Claims from './Claims/Claims';
import AccountsReceivable from './AccountsReceivable/AccountsReceivable';
import Remittances from './Remittances/Remittances';
import PrivatePayerBilling from './PrivatePayerBilling/PrivatePayerBilling';
import { AnalyticsBrowser } from '@segment/analytics-next';
import { SEGMENTAPIKEY } from '../../config';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import ComplianceDashboard from './Compliance/ComplianceDashboard';

const analytics = AnalyticsBrowser.load({ writeKey: SEGMENTAPIKEY });

const ToolbarAction = ({ title, label, icon, onClick, active, disabled }) => (
  <Tooltip title={title}>
    <Box
      onClick={disabled ? undefined : onClick}
      sx={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        mx: 0.25,
        px: 0.75,
        py: 0.5,
        borderRadius: 1,
        color: active ? 'primary.main' : 'action.active',
        bgcolor: active ? 'action.selected' : 'transparent',
        borderBottom: active ? '2px solid' : '2px solid transparent',
        borderColor: active ? 'primary.main' : 'transparent',
        transition: 'background-color 0.15s, color 0.15s',
        '&:hover': { bgcolor: 'action.hover' },
        ...(disabled && { pointerEvents: 'none', opacity: 0.4 }),
      }}
    >
      {icon}
      <Typography
        variant="caption"
        sx={{ fontSize: '0.65rem', lineHeight: 1.2, color: active ? 'primary.main' : 'text.secondary', mt: 0.25, userSelect: 'none' }}
      >
        {label}
      </Typography>
    </Box>
  </Tooltip>
);

class Billing extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      key: 'financialSummary',
      showInstructions: false,
      pBusinessType: this.props.user?.comp_settings.find(setting => setting.feature_value === 'primaryBusinessType') ? this.props.user.comp_settings.filter((setting) => setting.feature_value === "primaryBusinessType")[0].feature_id : null
    };
  }

  handleChangePage = (key) => {
    this.setState({ key, showInstructions: false });
  };

  toggleInstructions = () => {
    this.setState((prevState) => ({ showInstructions: !prevState.showInstructions }));
  };

  renderContent = () => {
    const commonProps = {
      checkTokenExpiry: this.props.checkTokenExpiry,
      user: this.props.user,
      comp_id: this.props.comp_id,
    };

    switch (this.state.key) {
      case 'accountsReceivable':
        return <AccountsReceivable {...commonProps} />;
      case 'compliance':
        return <ComplianceDashboard {...commonProps} />;
      case 'priorAuth':
        return <PreAuth {...commonProps} key={'priorAuth'} tabKey={this.state.key} />;
      case 'changeCapture':
        return <PreAuth {...commonProps} key={'changeCapture'} tabKey={this.state.key} />;
      case 'claims':
        return <Claims {...commonProps} />;
      case 'remittances':
        return <Remittances {...commonProps} />;
      case 'financialSummary':
        return <FinancialSummary {...commonProps} />;
      case 'privatePayerBilling':
        return <PrivatePayerBilling {...commonProps} />;
      default:
        return <FinancialSummary {...commonProps} />;
    }
  };

  getTitle = () => {
    switch (this.state.key) {
      case 'accountsReceivable':
        return 'Accounts Receivable';
      case 'compliance':
        return 'Quality Check';
      case 'priorAuth':
        return 'Prior Authorization';
      case 'changeCapture':
        return 'Change Capture';
      case 'claims':
        return 'Claims';
      case 'remittances':
        return 'Remittances';
      case 'financialSummary':
        return 'Financial Summary';
      case 'privatePayerBilling':
        return 'Private Payer Billing';
      default:
        return 'Financial Summary';
    }
  };

  render() {
    return (
      <Box sx={{ flexGrow: 1, height: '100%' }}>
        <AppBar position="static" color="default" sx={{ boxShadow: 'none', borderBottom: '1px solid #ccc' }}>
          <Toolbar sx={{ justifyContent: 'space-between' }}>
            <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Revenue Management
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle1" sx={{ color: 'text.secondary' }}>
                  {this.getTitle()}
                </Typography>
                {(this.state.key === 'claims' || this.state.key === 'priorAuth') && (
                  <IconButton
                    size="small"
                    onClick={this.toggleInstructions}
                    sx={{ padding: '4px' }}
                  >
                    <HelpOutlineIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              {this.state.showInstructions && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '60px',
                    left: 0,
                    zIndex: 10,
                    backgroundColor: 'white',
                    border: '1px solid #ccc',
                    borderRadius: '10px',
                    padding: '10px 15px',
                    fontFamily: 'Lato',
                    boxShadow: 3,
                    width: '300px',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 1 }}><b>How to Upload</b></Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    1. Download the template:
                    <a
                      href={this.state.key === 'claims' ? '/files/ClaimsUploadTemplate.csv' : '/files/pre_auth.csv'}
                      style={{ marginLeft: '5px', fontSize: '13px' }}
                    >
                      {this.state.key === 'claims' ? 'claims.csv' : 'pre_auth.csv'}
                    </a>
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    2. Fill out the template<br />
                    <span style={{ color: 'grey', fontSize: '12px' }}><i>If using Excel, save as .csv</i></span>
                  </Typography>
                  <Typography variant="body2">
                    3. Upload the file<br />
                    <span style={{ color: 'grey', fontSize: '12px' }}><i>Click the upload button and choose a file</i></span>
                  </Typography>
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ToolbarAction
                title="Financial Summary"
                label="Summary"
                icon={<PieChartIcon />}
                active={this.state.key === 'financialSummary'}
                onClick={() => this.handleChangePage('financialSummary')}
              />
              {this.props.user.permissions.AccountsReceivableTab && (
                <ToolbarAction
                  title="Accounts Receivable"
                  label="Receivables"
                  icon={<ReceiptIcon />}
                  active={this.state.key === 'accountsReceivable'}
                  onClick={() => this.handleChangePage('accountsReceivable')}
                />
              )}
              {this.props.user.permissions.PriorAuthorizationTab && (
                <ToolbarAction
                  title="Prior Authorization"
                  label="Prior Auth"
                  icon={<TaskIcon />}
                  active={this.state.key === 'priorAuth'}
                  disabled={![101, 102, 105].includes(this.state.pBusinessType)}
                  onClick={() => this.handleChangePage('priorAuth')}
                />
              )}
              {this.props.user.permissions.ChangeCaptureTab && (
                <ToolbarAction
                  title="Change Capture"
                  label="Change Capture"
                  icon={<ChangeCircleIcon />}
                  active={this.state.key === 'changeCapture'}
                  disabled={![101, 102, 105].includes(this.state.pBusinessType)}
                  onClick={() => this.handleChangePage('changeCapture')}
                />
              )}
              {this.props.user.type === 'Admin' && (
                <ToolbarAction
                  title="Quality Check"
                  label="Quality Check"
                  icon={<FactCheckOutlinedIcon />}
                  active={this.state.key === 'compliance'}
                  onClick={() => this.handleChangePage('compliance')}
                />
              )}
              {this.props.user.permissions.ClaimsTab && (
                <ToolbarAction
                  title="Claims"
                  label="Claims"
                  icon={<AttachFileIcon />}
                  active={this.state.key === 'claims'}
                  onClick={() => this.handleChangePage('claims')}
                />
              )}
              {this.props.user.permissions.RemittancesTab && (
                <ToolbarAction
                  title="Remittances"
                  label="Remittances"
                  icon={<ReceiptLongIcon />}
                  active={this.state.key === 'remittances'}
                  onClick={() => this.handleChangePage('remittances')}
                />
              )}
              {this.props.user.permissions.PrivatePayerBillingTab && (
                <ToolbarAction
                  title="Private Payer Billing"
                  label="Private Payer"
                  icon={<RequestQuoteOutlinedIcon />}
                  active={this.state.key === 'privatePayerBilling'}
                  onClick={() => this.handleChangePage('privatePayerBilling')}
                />
              )}
            </Box>
          </Toolbar>
        </AppBar>

        <Container
          maxWidth="80%"
          disableGutters
        >
          <Box sx={{ height: '100%', width: '100%' }}>{this.renderContent()}</Box>
        </Container>
      </Box>
    );
  }
}

export default Billing;
